import { useTranslation } from 'react-i18next'
import QuickRegisterAndFAQ from '@/components/homepage/quick-register-and-faq'
import StepsSection from '@/components/homepage/steps-seciton'
import {
  PricingIllustration,
  GameControllerIllustration,
  MapLocationIllustration,
  MobileTrackingIllustration,
} from '@/components/illustrations'
import { AudioLines, HeartHandshake, MapPinned } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function SolutionParticuliers() {
  const { t } = useTranslation(['solution-particuliers', 'home'])

  return (
    <>
      {/* Hero Section */}
      <section className="w-full min-h-[500px] bg-[#fdf6e9]">
        <div className="container mx-auto flex flex-col md:flex-row items-center justify-between py-12 px-4">
          {/* Left content */}
          <div className="md:w-1/2 w-full">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-primary leading-tight">
              {t('hero_title')}
            </h1>
            <p className="mb-8 text-gray-700 text-lg max-w-xl">
              {t('hero_description')}
            </p>
            <div className="flex gap-4">
              <Link
                to="/auth/signup"
                className="px-10 py-2 border border-gray-400 rounded hover:bg-gray-100 transition"
              >
                {t('common:register')}
              </Link>
              <Link
                to="/"
                className="px-10 py-2 border border-gray-400 rounded hover:bg-gray-100 transition"
              >
                {t('view_pricing')}
              </Link>
            </div>
          </div>
          {/* Right image */}
          <div className="md:w-1/2 w-full flex justify-center mt-10 md:mt-0">
            <div className="relative w-[500px] h-[350px]">
              <img
                src="https://fradaexpress.s3.eu-west-1.amazonaws.com/homepage/particulier.png"
                alt="Shipping Hero Illustration"
                width={350}
                height={350}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Feature 1 */}
          <div className="p-8 flex flex-col items-center">
            <div className="text-blue-500 flex items-center justify-center mb-4">
              <HeartHandshake className="w-10 h-10" />
            </div>
            <div className="text-gray-800 text-xl font-bold mb-2">
              {t('reliable_service')}
            </div>
            <div className="text-gray-700 text-center">
              {t('reliable_service_desc')}
            </div>
          </div>
          {/* Feature 2 */}
          <div className="p-8 flex flex-col items-center">
            <div className="text-blue-500 flex items-center justify-center mb-4">
              <MapPinned className="w-10 h-10" />
            </div>
            <div className="text-gray-800 text-xl font-bold mb-2">
              {t('fast_delivery')}
            </div>
            <div className="text-gray-700 text-center">
              {t('fast_delivery_desc')}
            </div>
          </div>
          {/* Feature 3 */}
          <div className="p-8 flex flex-col items-center">
            <div className="text-blue-500 flex items-center justify-center mb-4">
              <AudioLines className="w-10 h-10" />
            </div>
            <div className="text-gray-800 text-xl font-bold mb-2">
              {t('real_time_tracking')}
            </div>
            <div className="text-gray-700 text-center">
              {t('real_time_tracking_desc')}
            </div>
          </div>
        </div>
      </section>

      {/* Shipping Steps Section */}
      <StepsSection />

      {/* Pricing Section */}
      <section className="bg-[#eaf8ff] py-16">
        <div className="container mx-auto">
          <div className="max-w-6xl mx-auto bg-[#eaf8ff] rounded-lg p-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="md:w-1/2 w-full">
                <h2 className="text-4xl font-bold mb-6 text-primary">
                  {t('our_prices')}
                </h2>
                <p className="text-gray-700 mb-8 max-w-xl">
                  {t('price_description')}
                </p>
                <Link
                  to="/"
                  className="px-10 py-2 border border-gray-400 rounded hover:bg-gray-100 transition inline-block"
                >
                  {t('view_pricing')}
                </Link>
              </div>
              <div className="md:w-1/2 w-full flex justify-center mt-8 md:mt-0">
                <div className="relative w-[400px] h-[250px]">
                  <PricingIllustration />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold mb-10 text-center text-primary">
            {t('our_advantages')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Left content */}
            <div className="flex flex-col justify-center">
              <h3 className="text-xl font-bold text-primary mb-4">
                {t('easy_shipping_title')}
              </h3>
              <p className="text-gray-700 mb-4">{t('easy_shipping_desc')}</p>
            </div>
            {/* Right image */}
            <div>
              <div className="w-full h-[250px] relative">
                <GameControllerIllustration />
              </div>
            </div>

            {/* Right image */}
            <div className="">
              <div className="w-full h-[250px] relative">
                <MapLocationIllustration />
              </div>
            </div>
            {/* Left content */}
            <div className="flex flex-col justify-center">
              <h3 className="text-xl font-bold text-primary mb-4">
                {t('convenient_pickup_title')}
              </h3>
              <p className="text-gray-700 mb-4">
                {t('convenient_pickup_desc')}
              </p>
            </div>

            {/* Left content */}
            <div className="flex flex-col justify-center">
              <h3 className="text-xl font-bold text-primary mb-4">
                {t('real_time_tracking_title')}
              </h3>
              <p className="text-gray-700 mb-4">
                {t('real_time_tracking_long_desc')}
              </p>
            </div>
            {/* Right image */}
            <div>
              <div className="w-full h-[250px] relative">
                <MobileTrackingIllustration />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration and FAQ */}
      <QuickRegisterAndFAQ />
    </>
  )
}